// Editable widget grid for the Operations dashboard. Uses react-grid-layout
// for drag/resize in edit mode; static in view mode. Layout persists per user.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout';
import { Pencil, Check, Plus, X, RotateCcw, Settings } from 'lucide-react';
import 'react-grid-layout/css/styles.css';
import { WIDGET_BY_ID, withDefaults, type WidgetConfigField } from './widgets';
import { useDashboardLayout, type CustomSource } from './useDashboardLayout';

const ResponsiveGridLayout = WidthProvider(Responsive);

export function WidgetGrid({ selector, dashboard = 'operations', title = 'Health Operations', subtitle = 'Live operational overview', custom, actions }: {
  selector?: React.ReactNode; dashboard?: string; title?: string; subtitle?: string;
  /** Server-backed source for a custom dashboard (Part C). Omit = built-in view. */
  custom?: CustomSource;
  /** Extra toolbar actions (e.g. New / Share / Delete for custom dashboards). */
  actions?: React.ReactNode;
}) {
  const { t } = useTranslation('components');
  const { items, setLayout, addWidget, removeWidget, setWidgetConfig, reset, available } = useDashboardLayout(dashboard, custom);
  const [edit, setEdit] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Which widget's settings popover is open (by item id), in edit mode.
  const [settingsFor, setSettingsFor] = useState<string | null>(null);

  const onReset = () => { if (confirm(t('widgetGrid.resetConfirm'))) reset(); };

  return (
    <div className="space-y-3">
      {/* Toolbar — title + (passed-in) dashboard selector + edit controls, one row */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="text-sm text-muted">{edit ? t('widgetGrid.editModeHint') : subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {selector}
          {actions}
          {edit && (
            <div className="relative">
              <button onClick={() => setPickerOpen((o) => !o)} className="tk-btn-ghost px-2" disabled={available.length === 0}>
                <Plus size={16} /> {t('widgetGrid.addWidget')}
              </button>
              {pickerOpen && available.length > 0 && (
                <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-white/10 bg-surface p-1 shadow-2xl">
                  {available.map((w) => {
                    const Icon = w.icon;
                    return (
                      <button
                        key={w.id}
                        onClick={() => { addWidget(w.id); setPickerOpen(false); }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-muted hover:bg-white/5 hover:text-fg"
                      >
                        <Icon size={15} /> {w.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {edit && <button onClick={onReset} className="tk-btn-ghost px-2" title={t('widgetGrid.resetTooltip')}><RotateCcw size={16} /> {t('widgetGrid.reset')}</button>}
          <button onClick={() => { setEdit((e) => !e); setPickerOpen(false); }} className={edit ? 'tk-btn-primary' : 'tk-btn-ghost px-2'}>
            {edit ? <><Check size={16} /> {t('widgetGrid.done')}</> : <><Pencil size={16} /> {t('widgetGrid.edit')}</>}
          </button>
        </div>
      </div>

      {/* react-grid-layout positions tiles with absolute left:+translate using
          LTR column math; it has no RTL mode (under dir=rtl the tiles cluster /
          overlap). Pin the GRID wrapper to LTR so positioning stays correct, and
          the CSS rule `.tk-wgrid [dir='rtl'] &` re-flips each tile's CONTENT back
          to RTL in Hebrew so text/labels read right-to-left. */}
      <div className="tk-wgrid" dir="ltr">
      <ResponsiveGridLayout
        className="-mx-1"
        layouts={{ lg: items, md: items, sm: items }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={64}
        margin={[12, 12]}
        isDraggable={edit}
        isResizable={edit}
        draggableHandle=".widget-drag"
        onLayoutChange={(l: Layout[]) => edit && setLayout(l.map((x) => ({ i: x.i, x: x.x, y: x.y, w: x.w, h: x.h })))}
      >
        {items.map((it) => {
          const def = WIDGET_BY_ID[it.i];
          if (!def) return <div key={it.i} />;
          const hasSettings = !!def.configSchema?.length;
          return (
            <div key={it.i} className={`tk-wgrid-tile tk-card flex flex-col overflow-hidden ${edit ? 'ring-1 ring-primary/30' : ''}`}>
              {edit && (
                <div className="widget-drag -mx-1 -mt-1 mb-2 flex shrink-0 cursor-move items-center justify-between rounded-t-lg bg-white/5 px-2 py-1 text-[11px] text-muted">
                  <span className="truncate">{def.name}</span>
                  <div className="flex items-center gap-0.5">
                    {hasSettings && (
                      <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => setSettingsFor((cur) => (cur === it.i ? null : it.i))}
                        className="grid h-5 w-5 place-items-center rounded hover:bg-white/10 hover:text-fg"
                        title={t('widgetGrid.settingsTooltip')}
                      ><Settings size={12} /></button>
                    )}
                    <button onMouseDown={(e) => e.stopPropagation()} onClick={() => removeWidget(it.i)} className="grid h-5 w-5 place-items-center rounded hover:bg-white/10 hover:text-critical" title={t('widgetGrid.removeTooltip')}><X size={12} /></button>
                  </div>
                </div>
              )}
              {/* Settings popover (edit mode). Renders the widget's configSchema. */}
              {edit && hasSettings && settingsFor === it.i && (
                <WidgetSettings
                  fields={def.configSchema!}
                  config={it.config}
                  onChange={(patch) => setWidgetConfig(it.i, patch)}
                  onClose={() => setSettingsFor(null)}
                />
              )}
              {/* Content fills the tile; charts are responsive so no inner scroll. */}
              <div className="min-h-0 flex-1">{def.render(it.config)}</div>
            </div>
          );
        })}
      </ResponsiveGridLayout>
      </div>
    </div>
  );
}

/** Per-widget settings popover (Part B). Renders inputs from a widget's
 *  declarative configSchema; field labels/options are i18n keys. Writes patches
 *  up via onChange. Stops mousedown so RGL doesn't start a drag from inside. */
function WidgetSettings({ fields, config, onChange, onClose }: {
  fields: WidgetConfigField[];
  config: Record<string, unknown> | undefined;
  onChange: (patch: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('components');
  const eff = withDefaults(fields, config);
  return (
    <div
      className="absolute end-2 top-8 z-20 w-60 rounded-lg border border-white/10 bg-surface p-3 shadow-2xl"
      onMouseDown={(e) => e.stopPropagation()}
      dir="auto"
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-fg">{t('widgetGrid.settingsTitle')}</span>
        <button onClick={onClose} className="grid h-5 w-5 place-items-center rounded hover:bg-white/10"><X size={12} /></button>
      </div>
      <div className="space-y-2.5">
        {fields.map((f) => {
          const val = eff[f.key];
          if (f.type === 'toggle') {
            return (
              <label key={f.key} className="flex items-center justify-between gap-2 text-xs text-muted">
                <span>{t(f.label)}</span>
                <input type="checkbox" checked={!!val} onChange={(e) => onChange({ [f.key]: e.target.checked })} />
              </label>
            );
          }
          if (f.type === 'number') {
            return (
              <label key={f.key} className="block text-xs text-muted">
                <span className="mb-1 block">{t(f.label)}</span>
                <input
                  type="number" className="tk-input w-full py-1 text-sm"
                  value={Number(val)} min={f.min} max={f.max} step={f.step}
                  onChange={(e) => onChange({ [f.key]: e.target.value === '' ? f.default : Number(e.target.value) })}
                />
              </label>
            );
          }
          // select
          return (
            <label key={f.key} className="block text-xs text-muted">
              <span className="mb-1 block">{t(f.label)}</span>
              <select className="tk-input w-full py-1 text-sm" value={String(val)} onChange={(e) => onChange({ [f.key]: e.target.value })}>
                {f.options.map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
              </select>
            </label>
          );
        })}
      </div>
    </div>
  );
}
